import {
  BaseEmployeeDTORolesEnum,
  EmployeeDTOStatusEnum,
  TenantHierarchyDTO,
  TenantRoleDTORoleEnum,
  UserDTO,
  UserDTORolesEnum,
} from '../../api';
import { flattenTenants } from '../../utils/tenant';
import { apis } from '../api';
import { NamedTenant } from './hooks';

export function isGlobalAdmin(user: UserDTO | undefined): boolean {
  return !!user && (user.roles.includes(UserDTORolesEnum.Admin) || user.roles.includes(UserDTORolesEnum.SalesManager));
}

export interface AdminTenantAccessData {
  availableTenants: NamedTenant[];
  canSwitch: boolean;
  rootTenants: TenantHierarchyDTO[];
}

export function mayReadAllTenants(user: UserDTO | undefined): boolean {
  return (
    !!user &&
    (user.roles.includes(UserDTORolesEnum.Admin) ||
      user.roles.includes(UserDTORolesEnum.SalesManager) ||
      user.roles.includes(UserDTORolesEnum.SupportViewer))
  );
}

export async function maySwitchAdminTenant(user: UserDTO | undefined): Promise<boolean> {
  return (await getAdminTenantAccessData(user)).canSwitch;
}

export async function getUserTenantAdminRoles(user: UserDTO | undefined): Promise<NamedTenant[]> {
  return (await getAdminTenantAccessData(user)).availableTenants;
}

const TENANT_ADMIN_ROLES = new Set<TenantRoleDTORoleEnum>([
  TenantRoleDTORoleEnum.TenantAdmin,
  TenantRoleDTORoleEnum.TenantAssistant,
  TenantRoleDTORoleEnum.TenantSales,
]);

export async function getAdminTenantAccessData(user: UserDTO | undefined): Promise<AdminTenantAccessData> {
  const readAllTenants = mayReadAllTenants(user);

  const validEmployments = (user?.employments ?? []).filter(
    e =>
      e.status === EmployeeDTOStatusEnum.Active &&
      (e.roles.includes(BaseEmployeeDTORolesEnum.TenantAssistant) ||
        e.roles.includes(BaseEmployeeDTORolesEnum.TenantAdmin) ||
        readAllTenants),
  );

  const employmentTenantIds = validEmployments.map(e => e.tenantId);
  const rootTenants: TenantHierarchyDTO[] = employmentTenantIds.length
    ? (
        await apis.tenants.getTenantHierarchies({
          tenantIds: employmentTenantIds,
        })
      ).data.rootTenants
    : [];

  const allNodes = new Map(rootTenants.flatMap(flattenTenants).map(n => [n.tenant.id, n]));

  const employmentTenantSet = new Set(employmentTenantIds);

  // Source 1: tenants where the user has a direct active employment
  const employmentTenants = validEmployments.filter(e => e.tenantName != null).map(e => ({ id: e.tenantId, name: e.tenantName! }));

  // Source 2: child tenants reachable via employment hierarchy
  const childTenants = validEmployments.flatMap(employment => {
    const tenantNode = allNodes.get(employment.tenantId);
    if (!tenantNode) return [];

    return flattenTenants(tenantNode)
      .filter(t => t.tenant.id !== employment.tenantId)
      .map(t => ({ id: t.tenant.id, name: t.tenant.name }));
  });

  // Source 3: tenants where the user has an admin-level tenant role but no employment
  const tenantRoleTenants = (user?.tenantRoles ?? [])
    .filter(r => TENANT_ADMIN_ROLES.has(r.role) && r.tenantName != null && !employmentTenantSet.has(r.tenantId))
    .map(r => ({ id: r.tenantId, name: r.tenantName! }));

  // Merge all sources and deduplicate by tenant ID
  const availableTenants = Array.from(new Map([...employmentTenants, ...childTenants, ...tenantRoleTenants].map(t => [t.id, t])).values());

  return {
    availableTenants,
    canSwitch: readAllTenants || availableTenants.length > 1,
    rootTenants,
  };
}
